import { create } from 'zustand';
import { isAdminRole, isManagerRole } from '@leadgenos/shared';
import type { SessionUser, Roster } from '@leadgenos/shared';
import { api, setViewAs } from '../lib/api';
import { queryClient } from '../lib/queryClient';

/** A rep the manager/admin is impersonating via "View as Rep". */
export interface ActingRep {
  id: string;
  name: string;
}

interface SessionState {
  user: SessionUser | null;
  ready: boolean;
  /** Non-null when a manager/admin is viewing the app as a specific rep. */
  actingAs: ActingRep | null;
  setUser: (u: SessionUser | null) => void;
  bootstrap: () => Promise<void>;
  logout: () => Promise<void>;
  setActingAs: (rep: ActingRep | null) => void;
  /** Real account role helpers (ignore impersonation). */
  isManager: () => boolean;
  isAdmin: () => boolean;
  /** Effective helpers that honor "View as Rep" for UI gating. */
  effectiveRole: () => SessionUser['role'];
  isManagerView: () => boolean;
  isAdminView: () => boolean;
  /** The rep name whose data is effectively in scope (impersonated rep, else the real user). */
  scopeName: () => string | null;
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  ready: false,
  actingAs: null,
  setUser: (u) => set({ user: u }),
  bootstrap: async () => {
    try {
      const u = await api.me();
      set({ user: u, ready: true });
    } catch {
      set({ user: null, ready: true });
    }
  },
  logout: async () => {
    try {
      await api.logout();
    } finally {
      setViewAs(null);
      set({ user: null, actingAs: null });
      // Drop the previous user's cached data so the next login (e.g. on a shared machine) never
      // flashes stale, out-of-scope results before its own queries resolve.
      void queryClient.clear();
    }
  },
  setActingAs: (rep) => {
    // Only real managers/admins may impersonate; the server also enforces this.
    const r = get().user?.role;
    if (rep && (!r || !isManagerRole(r))) return;
    const prev = get().actingAs;
    if ((prev?.id ?? null) === (rep?.id ?? null)) return; // no-op, keep cache
    // Audit the impersonation change. Clear the X-View-As header BEFORE logging so the entry is
    // attributed to the real manager, not the rep being impersonated; then set the real target.
    setViewAs(null);
    if (rep) void api.logViewAs('view-as.start', rep.name).catch(() => {});
    else if (prev) void api.logViewAs('view-as.stop', prev.name).catch(() => {});
    setViewAs(rep?.id ?? null);
    set({ actingAs: rep });
    // Every cached query was fetched under the *previous* identity and none of the query keys are
    // scoped by the impersonated rep, so wipe the cache. Active screens immediately refetch with the
    // new X-View-As header; inactive ones refetch on next visit — no stale cross-rep data lingers.
    void queryClient.clear();
  },
  isManager: () => {
    const r = get().user?.role;
    return !!r && isManagerRole(r);
  },
  isAdmin: () => {
    const r = get().user?.role;
    return !!r && isAdminRole(r);
  },
  effectiveRole: () => (get().actingAs ? 'REP' : (get().user?.role ?? 'REP')),
  isManagerView: () => {
    if (get().actingAs) return false; // impersonating a rep → rep-level UI
    const r = get().user?.role;
    return !!r && isManagerRole(r);
  },
  isAdminView: () => !get().actingAs && get().isAdmin(),
  scopeName: () => get().actingAs?.name ?? get().user?.name ?? null,
}));

/** Convenience selector for components that just need the roster-friendly ActingRep list. */
export type { Roster };
